import React, { useContext, useEffect, useState } from "react"
import { Div, Col, Row, Text } from "atomize"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import { theme } from "../settings/config"
import Container from "../atoms/Container"
import { motion } from "framer-motion"
import { profileContext } from "../providers/profile.provider"
import {
	APP_CALENDAR_NAME,
	getGoogleCalendar,
	createGoogleCalendar,
	isReconnectError,
} from "../providers/profile.provider"
import { toast } from "react-toastify"
import { DateTime } from "luxon"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronLeft, faChevronRight } from "@fortawesome/free-solid-svg-icons"
import { networkAwareMessage } from "../utils/network.utils"
import { useSyncStatus } from "../services/sync.service"
import { checkCalendarNow } from "../services/calendarSync.service"

const monthName = month => month.setLocale("it").toFormat("LLLL yyyy")

const capitalized = text => text.charAt(0).toUpperCase() + text.slice(1)

// Cosa ha trovato il controllo, in una riga
export const calendarCheckMessage = ({ lessons, repaired, skipped, failed }, month) => {
	const parts = [
		lessons === 0
			? `Nessuna tua lezione a ${monthName(month)}.`
			: `${lessons} ${lessons === 1 ? "lezione" : "lezioni"} a ${monthName(month)}.`,
	]
	if (repaired > 0) {
		parts.push(repaired === 1 ? "Un evento sistemato." : `${repaired} eventi sistemati.`)
	} else if (lessons > 0 && !skipped && !failed) {
		parts.push("Tutte sul calendario: niente da sistemare.")
	}
	if (skipped > 0) {
		parts.push(
			skipped === 1
				? "Una non controllata: il tipo di lezione o il circolo non si leggono."
				: `${skipped} non controllate: il tipo di lezione o il circolo non si leggono.`,
		)
	}
	if (failed > 0) {
		parts.push(
			failed === 1
				? "Una non sistemata: Google l'ha rifiutata, si riprova domani."
				: `${failed} non sistemate: Google le ha rifiutate, si riprova domani.`,
		)
	}
	return parts.join(" ")
}

// Il controllo che l'app fa da sola ogni giorno, a mano e sul mese che si
// sceglie: per chi vuole esserne sicuro, o sistemare un mese lontano.
function CalendarCheck({ coach }) {
	const { online } = useSyncStatus()
	const [month, setMonth] = useState(() => DateTime.now().startOf("month"))
	const [isChecking, setIsChecking] = useState(false)
	const [result, setResult] = useState(null)

	const changeMonth = months => {
		setMonth(current => current.plus({ months }))
		setResult(null)
	}

	const handleCheck = async () => {
		setIsChecking(true)
		setResult(null)
		try {
			setResult(await checkCalendarNow(coach, month))
		} catch (err) {
			console.error("Controllo del calendario:", err)
			toast.error(
				isReconnectError(err)
					? "Collegamento con Google scaduto: autorizza di nuovo l'accesso"
					: networkAwareMessage(err, "Non sono riuscito a controllare il calendario"),
				{ position: "top-center" },
			)
		} finally {
			setIsChecking(false)
		}
	}

	return (
		<Div m={{ t: "1.25rem" }}>
			<Text textWeight="bold" textSize="subheader">
				Controllo del calendario
			</Text>
			<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "0.75rem" }}>
				Ogni giorno inCampo confronta le tue lezioni di questo mese e del prossimo con il
				calendario, e sistema gli eventi che mancano o sono rimasti indietro. Puoi farlo
				anche adesso, per il mese che vuoi.
			</Text>
			<Div d="flex" align="center" justify="space-between" style={{ gap: "0.5rem" }}>
				<Div d="flex" align="center" style={{ gap: "0.25rem" }}>
					<SecondaryButton
						m="0"
						h="2.5rem"
						textColor={theme.colors.gunmetal}
						text={<FontAwesomeIcon icon={faChevronLeft} />}
						disabled={isChecking}
						action={() => changeMonth(-1)}
					/>
					<Text textWeight="bold" style={{ minWidth: "8rem", textAlign: "center" }}>
						{capitalized(monthName(month))}
					</Text>
					<SecondaryButton
						m="0"
						h="2.5rem"
						textColor={theme.colors.gunmetal}
						text={<FontAwesomeIcon icon={faChevronRight} />}
						disabled={isChecking}
						action={() => changeMonth(1)}
					/>
				</Div>
				<PrimaryButton
					m="0"
					bg={theme.colors.gunmetal_fill}
					textColor="white"
					text={isChecking ? "Controllo..." : "Controlla ora"}
					disabled={isChecking || !online}
					action={handleCheck}
				/>
			</Div>
			{!online && (
				<Text textSize="caption" textColor={theme.colors.text_light} m={{ t: "0.5rem" }}>
					Senza rete Google non si raggiunge: il controllo si fa quando torna.
				</Text>
			)}
			{result && (
				<Text textSize="caption" m={{ t: "0.5rem" }}>
					{calendarCheckMessage(result, month)}
				</Text>
			)}
		</Div>
	)
}

export function GoogleCalendarModal({ closeModal }) {
	const { coach, connectGoogleCalendar, updateCalendarSelection } = useContext(profileContext)

	const isConnected = coach?.googleCalendar?.connected === true
	const needsReconnect = coach?.googleCalendar?.needsReconnect === true

	const [step, setStep] = useState(isConnected ? 1 : 0)
	const [isLoading, setIsLoading] = useState(false)
	const [calendarName, setCalendarName] = useState(coach?.googleCalendar?.calendarName ?? "")
	const [isPreparing, setIsPreparing] = useState(false)

	useEffect(() => {
		if (isConnected) ensureAppCalendar()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isConnected])

	// L'app può toccare solo i calendari che ha creato lei, quindi il
	// calendario giusto o c'è già o va fatto. Si parte da quello salvato e
	// gli si chiede se esiste: la risposta vale per tutti i casi: chi si
	// ricollega se lo ritrova invece di duplicarlo, chi arriva dal permesso
	// largo aveva scelto un calendario suo che ora non è più raggiungibile,
	// e chi si collega adesso non ne ha nessuno.
	const ensureAppCalendar = async () => {
		setIsPreparing(true)
		try {
			const savedID = coach?.googleCalendar?.calendarID

			const saved = savedID
				? await getGoogleCalendar(savedID).catch(err => {
						// un collegamento morto è un altro discorso, e lo
						// deve vedere chi guarda
						if (isReconnectError(err)) throw err
						return null
					})
				: null

			const calendar = saved ?? (await createGoogleCalendar(APP_CALENDAR_NAME))

			setCalendarName(calendar.summary ?? APP_CALENDAR_NAME)

			if (calendar.id !== savedID) {
				await updateCalendarSelection(calendar.id, calendar.summary ?? APP_CALENDAR_NAME)
			}
		} catch (err) {
			console.error("Errore preparazione calendario:", err)
			// il messaggio di Google, quando c'è: un errore muto costa più
			// tempo di quanto ne risparmi in eleganza
			toast.error(
				isReconnectError(err)
					? "Collegamento con Google scaduto: autorizza di nuovo l'accesso"
					: err.message || "Non sono riuscito a preparare il calendario",
				{ position: "top-center" },
			)
			if (isReconnectError(err)) setStep(0)
		} finally {
			setIsPreparing(false)
		}
	}

	const handleConnect = async () => {
		setIsLoading(true)
		try {
			await connectGoogleCalendar(
				coach?.googleCalendar?.calendarID ?? "primary",
				coach?.googleCalendar?.calendarName ?? APP_CALENDAR_NAME,
			)
			toast.success("Google Calendar collegato! 🗓️", { position: "top-center" })
			setStep(1)
			await ensureAppCalendar()
		} catch (err) {
			console.error(err)
			if (err?.message !== "Collegamento annullato") {
				toast.error(networkAwareMessage(err, "Errore durante il collegamento"), {
					position: "top-center",
				})
			}
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<Container>
			<Div
				d="flex"
				justify="space-between"
				m={{ b: "0.5rem" }}
				textWeight="bold"
				textSize="title"
				textColor={theme === "light" ? "text" : "text_alt"}>
				Google Calendar 🗓️
			</Div>

			<motion.div
				key={step}
				initial={{ opacity: 0, y: 5 }}
				animate={{ opacity: 1, y: 0 }}
				exit={{ opacity: 0 }}>
				{step === 0 && (
					<Text textSize="paragraph" m={{ b: "1rem" }}>
						{needsReconnect
							? "Il collegamento con Google è scaduto e le lezioni non vengono più sincronizzate. Autorizza di nuovo l'accesso per riprendere."
							: "Vuoi avere le lezioni sul calendario? Collega il tuo account Google per sincronizzarle!"}
					</Text>
				)}

				{step === 1 && (
					<>
						{isPreparing ? (
							<Text textColor="gray">Preparo il calendario...</Text>
						) : (
							<>
								<Text textSize="paragraph" m={{ b: "0.5rem" }}>
									Le lezioni che segni finiscono nel calendario{" "}
									<strong>{calendarName || APP_CALENDAR_NAME}</strong>, che
									inCampo ha creato per te dentro il tuo account Google.
								</Text>
								<Text textSize="caption" textColor={theme.colors.text_light}>
									È un calendario a parte: lo trovi tra i tuoi su Google, puoi
									nasconderlo o cambiargli colore quando vuoi, e inCampo non
									vede né tocca gli altri.
								</Text>
								<CalendarCheck coach={coach} />
							</>
						)}
					</>
				)}
			</motion.div>

			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					{!isLoading && (
						<Col p="0">
							<SecondaryButton
								m="auto"
								textColor={theme.colors.gunmetal}
								textSize="subheader"
								text="Chiudi"
								action={closeModal}
							/>
						</Col>
					)}
					{step === 0 && (
						<Col p="0">
							<PrimaryButton
								m="auto"
								bg={theme.colors.gunmetal_fill}
								textColor="white"
								textSize="subheader"
								text={isLoading ? "Caricamento..." : "Collega Google"}
								disabled={isLoading}
								action={handleConnect}
							/>
						</Col>
					)}
				</Row>
			</Div>
		</Container>
	)
}

export default GoogleCalendarModal
